/**
 * Run status: the shape `GET /api/runs/:runId` returns and TanStack Query
 * polls while a local run is active (see architecture section 9). Lives here
 * rather than in `executor` because `apps/web` polls it directly, and
 * `ui-contracts` is the frontend-safe contract surface (architecture
 * section 5) — the web app must not depend on the Foundry workspace/process
 * package to read its own run status.
 */
import { err, ErrorDetailSchema, ok } from "@exploitlens/trace-ir";
import { z } from "zod";
export { err, ok };
/**
 * Mirrors `@exploitlens/invariants`'s `GateNameSchema`/`GateStatusSchema`/
 * `GateResultSchema` field-for-field rather than importing them. Confirmed by
 * running `pnpm --filter @exploitlens/web build`: `@exploitlens/invariants`
 * re-exports `gates.ts`, which imports `@exploitlens/executor` for
 * `runPassed`, which pulls in `node:child_process`/`node:fs` — Vite hard-fails
 * bundling the moment anything in that chain (e.g. `hashCanonicalJson`'s
 * `createHash` from `@exploitlens/manifest`, reached the same way through
 * `@exploitlens/receipt`) is actually referenced, not just externalized with
 * a warning. `ui-contracts` is the one package `apps/web` is allowed to
 * import wholesale (architecture section 5), so it must stay free of every
 * package in that Node-only chain: `invariants`, `executor`, `manifest`,
 * `receipt`, `variants`. `@exploitlens/trace-ir`, `@exploitlens/effects`, and
 * `@exploitlens/aligner` have no such chain (confirmed the same way) and are
 * imported directly below and in `artifacts.ts`.
 */
export const GateNameSchema = z.enum([
    "baseline",
    "exact-replay",
    "legitimate-control",
    "neighborhood",
]);
export const GateStatusSchema = z.enum(["pass", "fail", "inconclusive"]);
export const GateResultSchema = z.object({
    gate: GateNameSchema,
    status: GateStatusSchema,
    evidenceRefs: z.array(z.string().min(1)),
    explanation: z.string().min(1),
});
/** Mirrors the execution lifecycle state diagram in architecture section 6. */
export const RunPhaseSchema = z.enum([
    "validate-manifest",
    "baseline",
    "patched-replay",
    "control",
    "normalize-align",
    "variants",
    "receipt",
    "complete",
]);
export const RunLifecycleStatusSchema = z.enum(["queued", "running", "invalid", "complete"]);
export const RunStatusSchema = z
    .object({
    runId: z.string().min(1),
    status: RunLifecycleStatusSchema,
    phase: RunPhaseSchema.optional(),
    progress: z.number().min(0).max(100),
    gates: z.array(GateResultSchema),
    error: ErrorDetailSchema.nullable(),
})
    .refine((run) => run.status !== "invalid" || run.error !== null, {
    message: "an invalid run must record the error that stopped it",
    path: ["error"],
});
//# sourceMappingURL=schema.js.map